import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Trash2, Inbox, CheckCircle2, Clock3 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const fmtDate = (iso) =>
  new Date(iso).toLocaleString("bg-BG", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });

export default function AdminDashboard() {
  const [enquiries, setEnquiries] = useState(null);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem("ra_token");
    navigate("/admin/login");
  }, [navigate]);

  const load = useCallback(async () => {
    try {
      const [{ data: me }, { data: list }] = await Promise.all([api.get("/auth/me"), api.get("/admin/enquiries")]);
      setUser(me);
      setEnquiries(list);
    } catch (err) {
      if (err.response?.status === 401) logout();
      else toast.error(formatApiError(err.response?.data?.detail));
    }
  }, [logout]);

  useEffect(() => {
    if (!localStorage.getItem("ra_token")) {
      navigate("/admin/login");
      return;
    }
    load();
  }, [load, navigate]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((e) => (e.id === id ? { ...e, status } : e)));
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Сигурни ли сте, че искате да изтриете това запитване?")) return;
    try {
      await api.delete(`/admin/enquiries/${id}`);
      setEnquiries((list) => list.filter((e) => e.id !== id));
      toast.success("Запитването е изтрито.");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const newCount = (enquiries || []).filter((e) => e.status === "new").length;

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-ink text-mist">
      <header className="border-b border-line bg-coal/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <img src="/logo.png" alt="RightAccount" className="h-9 w-auto" />
            <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-dim ml-4">Запитвания</span>
          </div>
          <div className="flex items-center gap-5">
            <button
              data-testid="admin-articles-link"
              onClick={() => navigate("/admin/articles")}
              className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs text-dim hover:text-gold hover:border-gold transition-colors duration-300"
            >
              Статии
            </button>
            <span data-testid="admin-user-email" className="hidden sm:block font-mono text-xs text-dim">{user?.email}</span>
            <button
              data-testid="admin-logout-button"
              onClick={logout}
              className="inline-flex items-center gap-2 border border-line px-4 py-2 text-xs text-dim hover:text-gold hover:border-gold transition-colors duration-300"
            >
              <LogOut size={14} /> Изход
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <div className="grid grid-cols-3 gap-4 mb-10">
          {[
            { icon: Inbox, label: "Общо запитвания", value: enquiries?.length ?? "—", testid: "stat-total" },
            { icon: Clock3, label: "Нови", value: enquiries ? newCount : "—", testid: "stat-new" },
            { icon: CheckCircle2, label: "Обработени", value: enquiries ? enquiries.length - newCount : "—", testid: "stat-done" },
          ].map((s) => (
            <div key={s.testid} data-testid={s.testid} className="border border-line bg-coal p-5 md:p-6">
              <s.icon size={18} className="text-gold mb-3" strokeWidth={1.5} />
              <p className="font-mono text-2xl md:text-3xl text-mist">{s.value}</p>
              <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-dim mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {enquiries === null ? (
          <p className="text-dim text-sm" data-testid="enquiries-loading">Зареждане…</p>
        ) : enquiries.length === 0 ? (
          <div data-testid="enquiries-empty" className="border border-line bg-coal p-16 text-center">
            <Inbox size={28} className="text-dim mx-auto mb-4" strokeWidth={1.5} />
            <p className="text-dim text-sm">Все още няма получени запитвания.</p>
          </div>
        ) : (
          <div className="border border-line bg-coal overflow-x-auto">
            <table data-testid="enquiries-table" className="w-full text-sm min-w-[900px]">
              <thead>
                <tr className="border-b border-line font-mono text-[10px] uppercase tracking-[0.2em] text-dim text-left">
                  <th className="px-5 py-4">Дата</th>
                  <th className="px-5 py-4">Име</th>
                  <th className="px-5 py-4">Контакт</th>
                  <th className="px-5 py-4">Фирма</th>
                  <th className="px-5 py-4">Услуга</th>
                  <th className="px-5 py-4">Съобщение</th>
                  <th className="px-5 py-4">Статус</th>
                  <th className="px-5 py-4" aria-label="Действия" />
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {enquiries.map((e) => (
                  <tr key={e.id} data-testid={`enquiry-row-${e.id}`} className="hover:bg-ink/60 transition-colors duration-200 align-top">
                    <td className="px-5 py-4 font-mono text-xs text-dim whitespace-nowrap">{fmtDate(e.created_at)}</td>
                    <td className="px-5 py-4 text-mist font-medium whitespace-nowrap">{e.name}</td>
                    <td className="px-5 py-4 text-dim text-xs">
                      <a href={`mailto:${e.email}`} className="text-gold hover:text-gold-light">{e.email}</a>
                      {e.phone && <p className="mt-1">{e.phone}</p>}
                    </td>
                    <td className="px-5 py-4 text-dim">{e.company || "—"}</td>
                    <td className="px-5 py-4 text-dim">{e.service || "—"}</td>
                    <td className="px-5 py-4 text-dim max-w-xs">{e.message}</td>
                    <td className="px-5 py-4">
                      <select
                        data-testid={`enquiry-status-${e.id}`}
                        value={e.status}
                        onChange={(ev) => setStatus(e.id, ev.target.value)}
                        className={`bg-ink border px-2.5 py-1.5 text-xs outline-none cursor-pointer transition-colors duration-200 ${
                          e.status === "new" ? "border-gold/60 text-gold" : "border-line text-dim"
                        }`}
                      >
                        <option value="new">Ново</option>
                        <option value="done">Обработено</option>
                      </select>
                    </td>
                    <td className="px-5 py-4">
                      <button
                        data-testid={`enquiry-delete-${e.id}`}
                        onClick={() => remove(e.id)}
                        className="text-dim hover:text-red-400 transition-colors duration-200"
                        aria-label="Изтрий"
                      >
                        <Trash2 size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>
    </div>
  );
}
